import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/lib/orpc";
import { menuLetterhead } from "@/routes/_authenticated/menus/-lib/menu-letterhead";
import { readMenuPrint } from "@/routes/_authenticated/menus/-lib/menu-print";

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(message: string): void };
  }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/u;

interface PrintJob {
  facilityId: string;
  from: string;
  to: string;
  /** The router parses a JSON value in the address before it gets here. */
  options: unknown;
}

const text = (value: unknown) => (typeof value === "string" ? value : "");

/* The printed menu made for the phone app, which cannot run the PDF engine itself: the app opens
   this page out of sight and takes the finished file back. The same renderer as the print dialog,
   so the phone's PDF is the web's. */
export const Route = createFileRoute("/_authenticated/menu-print-file")({
  validateSearch: (search: Record<string, unknown>): PrintJob => ({
    facilityId: text(search.facilityId),
    from: text(search.from),
    to: text(search.to),
    options: search.options,
  }),
  component: MenuPrintFile,
});

function post(message: Record<string, unknown>) {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ menuPrint: true, ...message }));
}

async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let at = 0; at < bytes.length; at += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  }
  return btoa(binary);
}

function MenuPrintFile() {
  const { facilityId, from, to, options: chosen } = Route.useSearch();
  const queryClient = useQueryClient();
  const [state, setState] = useState("Preparing the menu");
  const named = Boolean(facilityId) && DAY.test(from) && DAY.test(to);
  const choices = JSON.stringify(chosen ?? null);

  useEffect(() => {
    if (!named) {
      post({ ok: false, message: "The menu to print was not named." });
      return;
    }
    const make = async () => {
      const [letterhead, menu, { renderMenuPdf }] = await Promise.all([
        menuLetterhead(queryClient, facilityId),
        queryClient.fetchQuery(
          orpc.agents.menus.menu.get.queryOptions({
            input: { facilityId, from, to },
          }),
        ),
        import("@/routes/_authenticated/menus/-lib/render-menu-pdf"),
      ]);
      const file = await renderMenuPdf({
        letterhead,
        from,
        to,
        days: menu.days,
        options: readMenuPrint(JSON.parse(choices)),
      });
      post({ ok: true, name: file.name, data: await base64Of(file) });
      setState("Ready");
    };
    make().catch(() => {
      post({ ok: false, message: "The menu could not be made. Try again." });
      setState("The menu could not be made");
    });
  }, [named, facilityId, from, to, choices, queryClient]);

  return (
    <>
      <h1 className="sr-only">Menu file</h1>
      <p className="text-muted-foreground p-6 text-sm">{named ? state : "Nothing to print"}</p>
    </>
  );
}
